(() => {
  const marker = 'data-theme-pointer-focus';
  const interactiveSelector = 'a, button, summary, [role="button"], [tabindex]';
  let pointerTarget = null;
  let pointerControl = null;
  let pointerIsDown = false;
  let pointerClick = false;
  let clickResetTimer = null;
  let programmaticFocusTarget = null;
  let lastPointerFocus = null;
  let historyFocus = null;
  let historyRestorePending = false;
  let historyResetTimer = null;

  const markPointerFocus = (element) => {
    element?.setAttribute?.(marker, '');
    lastPointerFocus = element;
  };

  const clearClick = () => {
    pointerClick = false;
    pointerTarget = null;
    pointerControl = null;
    programmaticFocusTarget = null;
    if (clickResetTimer !== null) {
      window.clearTimeout(clickResetTimer);
      clickResetTimer = null;
    }
  };

  const isPointerTarget = (target) =>
    target === pointerTarget || pointerTarget?.contains?.(target) || target?.contains?.(pointerTarget);

  // Call only at intentional focus moves made by a component during a click.
  // Unrelated focusin events never inherit pointer origin from that click.
  window.themeFocusModality = {
    markProgrammaticFocus(target) {
      if (!pointerClick || !target) return;
      programmaticFocusTarget = target;
      markPointerFocus(target);
    }
  };

  document.addEventListener('pointerdown', (event) => {
    clearClick();
    pointerIsDown = true;
    pointerTarget = event.target;
    pointerControl = event.target?.closest?.(interactiveSelector) || null;
    // Clicking a control that is already keyboard-focused may not emit focusin.
    document.activeElement?.setAttribute?.(marker, '');
    lastPointerFocus = null;
    if (pointerControl) markPointerFocus(pointerControl);
  }, true);

  document.addEventListener('pointerup', () => {
    pointerIsDown = false;
  }, true);

  document.addEventListener('pointercancel', () => {
    pointerIsDown = false;
    clearClick();
  }, true);

  // A real pointer click can synchronously move focus into or out of a dialog.
  // Keep that provenance for this click task, even after a long touch press.
  document.addEventListener('click', (event) => {
    pointerClick = Boolean(pointerTarget && event.detail > 0 && isPointerTarget(event.target));
    if (pointerClick) {
      const trigger = event.target?.closest?.(interactiveSelector);
      if (trigger) markPointerFocus(trigger);
    }
    if (clickResetTimer !== null) window.clearTimeout(clickResetTimer);
    clickResetTimer = window.setTimeout(clearClick, 0);
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.metaKey || event.ctrlKey || event.altKey || ['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) return;
    pointerIsDown = false;
    clearClick();
    document.activeElement?.removeAttribute?.(marker);
    lastPointerFocus = null;
    historyRestorePending = false;
  }, true);

  document.addEventListener('focusin', (event) => {
    if ((pointerIsDown && pointerControl &&
         (event.target === pointerControl || pointerControl.contains(event.target))) ||
        event.target === programmaticFocusTarget ||
        (historyRestorePending && event.target === historyFocus)) {
      markPointerFocus(event.target);
    } else {
      event.target.removeAttribute(marker);
      lastPointerFocus = null;
    }
    if (event.target === programmaticFocusTarget) programmaticFocusTarget = null;
    if (historyRestorePending) historyRestorePending = false;
  }, true);

  // The back/forward cache may restore a pointer-clicked card after pageshow.
  window.addEventListener('pagehide', () => {
    historyFocus = lastPointerFocus?.isConnected ? lastPointerFocus : null;
  });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted || !historyFocus?.isConnected) return;
    historyRestorePending = true;
    if (document.activeElement === historyFocus) historyFocus.setAttribute(marker, '');
    if (historyResetTimer !== null) window.clearTimeout(historyResetTimer);
    historyResetTimer = window.setTimeout(() => {
      historyRestorePending = false;
      historyResetTimer = null;
    }, 100);
  });
})();
